import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@chakra-ui/react';

const VIEWPORT_MARGIN_PX = 8;

/** Hover open with a short close delay so the pointer can cross into a portaled menu. */
export function useNavSubmenuHover() {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);

  const clear = () => {
    if (timer.current != null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => clear, []);

  return {
    open,
    show: () => {
      clear();
      setOpen(true);
    },
    hide: () => {
      clear();
      timer.current = window.setTimeout(() => setOpen(false), 140);
    },
  };
}

/**
 * Desktop nav menus portal to the body so a horizontally scrolling link row
 * does not clip them. The anchor is the trigger's wrapper.
 */
export function NavSubmenuPortal({
  open,
  anchorRef,
  label,
  minW,
  onMouseEnter,
  onMouseLeave,
  children,
}: {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  label: string;
  minW: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  children: ReactNode;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos((prev) => (prev == null ? prev : null));
      return undefined;
    }
    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuWidth = menuRef.current?.offsetWidth ?? 0;
      const maxLeft = window.innerWidth - menuWidth - VIEWPORT_MARGIN_PX;
      const left = Math.round(Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.left, maxLeft)));
      const top = Math.round(rect.bottom);
      setPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRef, open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <Box
      ref={menuRef}
      position="fixed"
      top={pos ? `${pos.top}px` : '0px'}
      left={pos ? `${pos.left}px` : '0px'}
      visibility={pos ? 'visible' : 'hidden'}
      zIndex={60}
      minW={minW}
      pt={1.5}
      role="menu"
      aria-label={label}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </Box>,
    document.body,
  );
}
